<template>
  <div class="search-page">
    <AppHeader />

    <section class="search-page__hero">
      <h1 class="search-page__title">全库检索</h1>
      <div class="search-page__bar">
        <input
          v-model="keyword"
          type="text"
          class="search-page__input"
          placeholder="请输入关键词"
          @keyup.enter="handleSearch"
        />
        <button type="button" class="search-page__btn" @click="handleSearch">搜索</button>
      </div>
    </section>

    <section class="search-page__body">
      <div class="search-page__inner">
        <div v-if="loading" class="search-page__loading">检索中...</div>

        <template v-else>
          <div class="search-page__section">
            <div class="search-page__section-head">
              <h2>史料知识</h2>
              <router-link
                v-if="keyword"
                :to="{ path: '/knowledge', query: { keyword } }"
                class="search-page__more"
              >
                查看更多
              </router-link>
            </div>
            <KnowledgeResultList :list="knowledgeList" view-mode="list" />
          </div>

          <div class="search-page__section">
            <div class="search-page__section-head">
              <h2>馆藏资源</h2>
              <router-link
                v-if="keyword"
                :to="{ path: '/resources', query: { keyword } }"
                class="search-page__more"
              >
                查看更多
              </router-link>
            </div>
            <ResourcesResultList :list="resourceList" view-mode="list" />
          </div>

          <div class="search-page__section">
            <div class="search-page__section-head">
              <h2>主题展览</h2>
              <router-link
                v-if="keyword"
                :to="{ path: '/exhibition', query: { keyword } }"
                class="search-page__more"
              >
                查看更多
              </router-link>
            </div>
            <div v-if="!exhibitionList.length" class="search-page__empty">暂无相关主题展览</div>
            <div v-else class="search-page__exhibition-list">
              <article
                v-for="item in exhibitionList"
                :key="item.id"
                class="search-page__exhibition-card"
              >
                <img v-if="item.image" :src="item.image" :alt="item.title">
                <div class="search-page__exhibition-body">
                  <h3>{{ item.title }}</h3>
                  <p>{{ item.description }}</p>
                  <router-link :to="`/exhibition/${item.id}`">查看专题 &gt;</router-link>
                </div>
              </article>
            </div>
          </div>
        </template>
      </div>
    </section>

    <AppFooter />
  </div>
</template>

<script>
import AppHeader from '@/components/common/AppHeader.vue'
import AppFooter from '@/components/common/AppFooter.vue'
import KnowledgeResultList from '@/views/knowledge/components/ResultList.vue'
import ResourcesResultList from '@/views/resources/components/ResultList.vue'
import { searchKnowledgeEntities, mapKnowledgeItem } from '@/api/knowledge'
import { searchResources, mapResourceItem } from '@/api/resources'
import { fetchExhibitionList, mapExhibitionItem } from '@/api/exhibition'

export default {
  name: 'SearchPage',
  components: {
    AppHeader,
    AppFooter,
    KnowledgeResultList,
    ResourcesResultList
  },
  data() {
    return {
      keyword: '',
      loading: false,
      knowledgeList: [],
      resourceList: [],
      exhibitionList: []
    }
  },
  watch: {
    '$route.query.keyword': {
      immediate: true,
      handler(value) {
        this.keyword = value || ''
        this.fetchAll()
      }
    }
  },
  methods: {
    handleSearch() {
      const keyword = this.keyword.trim()
      this.$router.replace({
        path: '/search',
        query: keyword ? { keyword } : {}
      }).catch(() => {})
      this.fetchAll()
    },
    async fetchAll() {
      const keyword = this.keyword.trim()
      if (!keyword) {
        this.knowledgeList = []
        this.resourceList = []
        this.exhibitionList = []
        return
      }
      this.loading = true
      try {
        const [knowledgeRes, resourceRes, exhibitionRes] = await Promise.all([
          searchKnowledgeEntities({ page: 1, limit: 5, keyword }),
          searchResources({ page: 1, limit: 5, keyword, matchMode: 'fuzzy' }),
          fetchExhibitionList({ page: 1, limit: 5, title: keyword, status: '1,2' })
        ])
        this.knowledgeList = (knowledgeRes.records || []).map(mapKnowledgeItem)
        this.resourceList = (resourceRes.records || []).map(mapResourceItem)
        this.exhibitionList = (exhibitionRes.records || []).map(mapExhibitionItem)
      } catch (error) {
        this.knowledgeList = []
        this.resourceList = []
        this.exhibitionList = []
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.search-page {
  min-height: 100vh;
  background: $color-paper;

  &__hero {
    padding: calc(#{$header-height} + #{$spacing-3xl}) $content-padding $spacing-2xl;
    background: $color-paper-dark;
    text-align: center;
  }

  &__title {
    font-family: $font-serif;
    font-size: rem(32);
    color: $color-brown-dark;
    margin-bottom: $spacing-lg;
  }

  &__bar {
    display: flex;
    max-width: 720px;
    margin: 0 auto;
    background: $color-white;
    border-radius: 4px;
    overflow: hidden;
    box-shadow: 0 4px 16px rgba($color-brown-dark, 0.08);
  }

  &__input {
    flex: 1;
    padding: 0 $spacing-base;
    border: none;
    min-height: 48px;
    font-size: rem(14);
  }

  &__btn {
    padding: 0 $spacing-xl;
    background: $success-color;
    color: $color-white;
    font-size: rem(14);
  }

  &__body {
    padding: $spacing-2xl $content-padding $spacing-4xl;
  }

  &__inner {
    max-width: $container-max-width;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: $spacing-2xl;
  }

  &__loading,
  &__empty {
    padding: $spacing-xl;
    text-align: center;
    color: $color-text-light;
    background: $color-white;
    border-radius: 4px;
  }

  &__section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: $spacing-base;

    h2 {
      font-family: $font-serif;
      font-size: rem(22);
      color: $color-brown-dark;
    }
  }

  &__more {
    font-size: rem(14);
    color: $success-color;
  }

  &__exhibition-list {
    display: grid;
    gap: $spacing-base;
  }

  &__exhibition-card {
    display: flex;
    gap: $spacing-base;
    padding: $spacing-base;
    background: $color-white;
    border-radius: 4px;

    img {
      width: 160px;
      height: 110px;
      object-fit: cover;
      flex-shrink: 0;
    }
  }

  &__exhibition-body {
    min-width: 0;

    h3 {
      font-size: rem(16);
      margin-bottom: $spacing-xs;
      color: $color-brown-dark;
    }

    p {
      font-size: rem(13);
      line-height: 1.7;
      color: $color-text-light;
      margin-bottom: $spacing-sm;
    }

    a {
      font-size: rem(13);
      color: $success-color;
    }
  }
}
</style>
